// View 1: Address Entry

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AddressAutocomplete } from '@/components/forms/AddressAutocomplete';
import { OrderStatusModal } from '@/components/OrderStatusModal';
import { useOrderStore } from '@/stores/orderStore';
import { generateUserId } from '@/services/userService';
import { checkWaterServiceability } from '@/services/serviceabilityService';
// import { fetchPropertyData } from '@/services/propertyService'; // RETIRED (Sep 2026): RentCast
import { fetchInternetPlans, fetchElectricityPlans } from '@/services/plansService';
import { clearUserData } from '@/utils/storage';
import { getFlowType } from '@/config/abTest';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import { consumeSessionExpiredFlag } from '@/utils/sessionExpired';
import { skipsServiceAvailableScreen /*, shouldShowPropertyScreen */ } from '@/utils/serviceabilityFlow';
import type { AddressData } from '@/types';

type ManualAddressFields = {
  address1: string;
  address2: string;
  city: string;
  stateAbbr: string;
  zip: string;
};

type ManualAddressErrors = Partial<Record<keyof ManualAddressFields, string>>;

const emptyManualAddress: ManualAddressFields = {
  address1: '',
  address2: '',
  city: '',
  stateAbbr: '',
  zip: '',
};

const isFilled = (value: string) => value.trim().length > 0;

const hasManualAddressMinimum = (fields: ManualAddressFields) =>
  isFilled(fields.address1) &&
  isFilled(fields.city) &&
  isFilled(fields.stateAbbr) &&
  isFilled(fields.zip);

const validateManualAddress = (fields: ManualAddressFields): ManualAddressErrors => {
  const errors: ManualAddressErrors = {};

  if (!isFilled(fields.address1)) {
    errors.address1 = 'Address 1 is required.';
  }

  if (!isFilled(fields.city)) {
    errors.city = 'City is required.';
  }

  if (!/^[A-Z]{2}$/.test(fields.stateAbbr.trim().toUpperCase())) {
    errors.stateAbbr = 'Use a 2-letter state abbreviation.';
  }

  if (!/^\d{5}$/.test(fields.zip.trim())) {
    errors.zip = 'Use a 5-digit ZIP code.';
  }

  return errors;
};

const buildManualAddressData = (fields: ManualAddressFields): AddressData => {
  const address1 = fields.address1.trim();
  const address2 = fields.address2.trim();
  const city = fields.city.trim();
  const stateCode = fields.stateAbbr.trim().toUpperCase();
  const zip = fields.zip.trim();
  const streetParts = address1.split(/\s+/);
  const streetNumber = streetParts[0] || '';
  const route = streetParts.slice(1).join(' ') || address1;
  const fullAddress = address2
    ? `${address1} ${address2}, ${city}, ${stateCode} ${zip}`
    : `${address1}, ${city}, ${stateCode} ${zip}`;

  return {
    street: address1,
    streetNumber,
    route,
    unit: address2 || undefined,
    city,
    state: stateCode,
    stateCode,
    zip,
    country: 'US',
    fullAddress,
  };
};

export function AddressEntryView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAddress: storeAddress, setUserId, setWaterAvailable, /* setPropertyData, */ setFlowType, reset } = useOrderStore();

  // Full reset when user arrives at address entry (Step 1)
  useEffect(() => {
    reset();                    // Clear Zustand store
    queryClient.clear();        // Clear React Query cache
    clearUserData();            // Clear legacy sessionStorage keys
    logger.info({ area: 'address' }, 'Full state reset');

    const params = new URLSearchParams(window.location.search);
    track('checkout_page_landed', {
      referrer: document.referrer,
      url: window.location.href,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    });
  }, [reset, queryClient]);

  const [address, setAddress] = useState('');
  const [unit, setUnit] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<AddressData | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [manualAddress, setManualAddress] = useState<ManualAddressFields>(emptyManualAddress);
  const [manualErrors, setManualErrors] = useState<ManualAddressErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOrderStatus, setShowOrderStatus] = useState(false);
  const sessionExpiredOnMount = useRef(consumeSessionExpiredFlag()).current;
  const [showSessionNotice, setShowSessionNotice] = useState(sessionExpiredOnMount);
  const canSubmit = isManualEntry
    ? hasManualAddressMinimum(manualAddress)
    : !!selectedAddress;

  const handleAddressSelect = (addressData: AddressData) => {
    setIsManualEntry(false);
    setManualErrors({});
    setSelectedAddress(addressData);
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    setSelectedAddress(null);
  };

  const handleManualEntryRequest = (currentInput: string) => {
    setIsManualEntry(true);
    setSelectedAddress(null);
    setManualErrors({});
    setManualAddress((current) => ({
      address1: current.address1 || currentInput,
      address2: current.address2 || unit,
      city: current.city,
      stateAbbr: current.stateAbbr,
      zip: current.zip,
    }));
  };

  const handleManualSearchInstead = () => {
    setIsManualEntry(false);
    setManualErrors({});
    setAddress(manualAddress.address1);
    setUnit(manualAddress.address2);
  };

  const updateManualAddress = (field: keyof ManualAddressFields, value: string) => {
    const nextValue =
      field === 'stateAbbr'
        ? value.replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase()
        : field === 'zip'
          ? value.replace(/\D/g, '').slice(0, 5)
          : value;

    setManualAddress((current) => ({
      ...current,
      [field]: nextValue,
    }));

    if (manualErrors[field]) {
      setManualErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    let addressData: AddressData;

    if (isManualEntry) {
      const errors = validateManualAddress(manualAddress);
      if (Object.keys(errors).length > 0) {
        setManualErrors(errors);
        return;
      }

      addressData = buildManualAddressData(manualAddress);
    } else {
      if (!selectedAddress) return;

      addressData = { ...selectedAddress };

      if (unit.trim() && !addressData.unit) {
        addressData.unit = unit.trim();
      }
    }

    setIsSubmitting(true);

    try {
      // Generate user ID if we don't have one
      let userId = useOrderStore.getState().userId;
      if (!userId) {
        userId = await generateUserId();
        if (userId) {
          setUserId(userId);
        }
      }

      // Store address in Zustand
      storeAddress(addressData);

      // Determine A/B test flow type based on zip code
      const flowType = getFlowType(addressData.zip);
      setFlowType(flowType);
      logger.info({ area: 'address' }, `A/B Test flow type: ${flowType} (zip: ${addressData.zip})`);

      // Start background fetches (non-blocking) - these prefetch data for later views
      // Property data (RentCast) — RETIRED (Sep 2026), see services/propertyService.ts
      // const propertyDataPromise = fetchPropertyData(addressData).then((result) => {
      //   if (result.success && result.propertyData) {
      //     setPropertyData(result.propertyData);
      //     logger.info({ area: 'address' }, 'Background property data fetched');
      //     return result.propertyData;
      //   }
      //   return null;
      // }).catch((err) => {
      //   logger.error({ area: 'address' }, 'Background property fetch failed', err instanceof Error ? err : undefined);
      //   return null;
      // });

      // Prefetch internet plans into React Query cache
      queryClient.prefetchQuery({
        queryKey: ['internetPlans', addressData.fullAddress, userId],
        queryFn: () => fetchInternetPlans(addressData, userId || undefined),
      });

      // Prefetch electricity plans into React Query cache
      queryClient.prefetchQuery({
        queryKey: ['electricityPlans', addressData.zip, userId, 1000], // Default 1000 kWh estimate
        queryFn: () => fetchElectricityPlans(addressData, null, null, userId || undefined, 1000),
      });

      // Check water serviceability (blocking - wait for result)
      const result = await checkWaterServiceability(addressData, userId || undefined);
      setWaterAvailable(result.waterAvailable);

      track('checkout_address_entered', {
        zip: addressData.zip,
        city: addressData.city,
        state: addressData.stateCode,
        has_unit: !!addressData.unit,
        water_available: result.waterAvailable,
        flow_type: flowType,
      });

      // Navigate based on water availability
      if (result.waterAvailable) {
        if (skipsServiceAvailableScreen(result.serviceData?.vendorName)) {
          // Vendors like Dallas, TX proceed straight into the order without
          // the "Great news" confirmation screen
          logger.info({ area: 'address' }, `Skipping service-available screen for vendor: ${result.serviceData?.vendorName}`);
          // RETIRED (Sep 2026): RentCast property confirmation screen
          // const propertyData = await propertyDataPromise;
          // if (shouldShowPropertyScreen(propertyData)) {
          //   navigate('/property');
          // } else if (flowType === 'B') {
          if (flowType === 'B') {
            navigate('/utilities');
          } else {
            navigate('/personal-info');
          }
        } else {
          navigate('/service-available');
        }
      } else {
        // Skip water service screen - use flow-aware navigation
        // Flow A: goes to /personal-info
        // Flow B: goes to /utilities (since utilities come before personal info)
        if (flowType === 'B') {
          navigate('/utilities');
        } else {
          navigate('/personal-info');
        }
      }
    } catch (error) {
      logger.error({ area: 'address' }, 'Error checking address', error instanceof Error ? error : undefined);
      track('checkout_error', {
        error_context: 'address_submit',
        error_message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        {showSessionNotice && (
          <div className="info-notice mb-4" role="status">
            <svg className="info-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <div className="info-notice__content">
              <p className="info-notice__text">
                Your previous session was cleared for your security. Please enter your address to start a new order.
              </p>
            </div>
            <button
              type="button"
              className="info-notice__dismiss"
              onClick={() => setShowSessionNotice(false)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <h1 className="text--center mb-3">Where are you moving?</h1>
        <p className="text--center mb-5">
          Enter in the street address for where you'll need service turned on.
        </p>

        <div className="form-group">
          <label htmlFor="address" className="form-label">Address</label>
          <AddressAutocomplete
            value={address}
            onChange={handleAddressChange}
            onAddressSelect={handleAddressSelect}
            onManualEntryRequest={handleManualEntryRequest}
            manualEntryActive={isManualEntry}
            placeholder="Start typing your address..."
          />
        </div>

        {isManualEntry ? (
          <div className="manual-address-form">
            <div className="manual-address-form__header">
              <div className="manual-address-form__title">Manual address</div>
              <button
                type="button"
                className="manual-address-form__switch"
                onClick={handleManualSearchInstead}
              >
                Search instead
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="manual-address-1" className="form-label">
                <span>ADDRESS 1 <span className={`required ${isFilled(manualAddress.address1) ? 'filled' : ''}`}>*</span></span>
              </label>
              <input
                type="text"
                id="manual-address-1"
                name="manual-address-1"
                value={manualAddress.address1}
                onChange={(e) => updateManualAddress('address1', e.target.value)}
                className={isFilled(manualAddress.address1) ? 'field-filled' : ''}
                aria-invalid={manualErrors.address1 ? 'true' : undefined}
                aria-describedby={manualErrors.address1 ? 'manual-address-1-error' : undefined}
                required
              />
              {manualErrors.address1 && (
                <span id="manual-address-1-error" className="form-error">{manualErrors.address1}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="manual-address-2" className="form-label">ADDRESS 2</label>
              <input
                type="text"
                id="manual-address-2"
                name="manual-address-2"
                placeholder="Apt 2B, Unit 5, etc."
                value={manualAddress.address2}
                onChange={(e) => updateManualAddress('address2', e.target.value)}
                className={isFilled(manualAddress.address2) ? 'field-filled' : ''}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="manual-city" className="form-label">
                  <span>CITY <span className={`required ${isFilled(manualAddress.city) ? 'filled' : ''}`}>*</span></span>
                </label>
                <input
                  type="text"
                  id="manual-city"
                  name="manual-city"
                  value={manualAddress.city}
                  onChange={(e) => updateManualAddress('city', e.target.value)}
                  className={isFilled(manualAddress.city) ? 'field-filled' : ''}
                  aria-invalid={manualErrors.city ? 'true' : undefined}
                  aria-describedby={manualErrors.city ? 'manual-city-error' : undefined}
                  required
                />
                {manualErrors.city && (
                  <span id="manual-city-error" className="form-error">{manualErrors.city}</span>
                )}
              </div>

              <div className="form-row form-row--compact">
                <div className="form-group">
                  <label htmlFor="manual-state" className="form-label">
                    <span>STATE <span className={`required ${isFilled(manualAddress.stateAbbr) ? 'filled' : ''}`}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="manual-state"
                    name="manual-state"
                    value={manualAddress.stateAbbr}
                    onChange={(e) => updateManualAddress('stateAbbr', e.target.value)}
                    className={isFilled(manualAddress.stateAbbr) ? 'field-filled' : ''}
                    maxLength={2}
                    autoCapitalize="characters"
                    aria-invalid={manualErrors.stateAbbr ? 'true' : undefined}
                    aria-describedby={manualErrors.stateAbbr ? 'manual-state-error' : undefined}
                    required
                  />
                  {manualErrors.stateAbbr && (
                    <span id="manual-state-error" className="form-error">{manualErrors.stateAbbr}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="manual-zip" className="form-label">
                    <span>ZIPCODE <span className={`required ${isFilled(manualAddress.zip) ? 'filled' : ''}`}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="manual-zip"
                    name="manual-zip"
                    value={manualAddress.zip}
                    onChange={(e) => updateManualAddress('zip', e.target.value)}
                    className={isFilled(manualAddress.zip) ? 'field-filled' : ''}
                    inputMode="numeric"
                    maxLength={5}
                    aria-invalid={manualErrors.zip ? 'true' : undefined}
                    aria-describedby={manualErrors.zip ? 'manual-zip-error' : undefined}
                    required
                  />
                  {manualErrors.zip && (
                    <span id="manual-zip-error" className="form-error">{manualErrors.zip}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="form-group">
            <label htmlFor="apt-suite" className="form-label">Apt/Suite (optional)</label>
            <input
              type="text"
              id="apt-suite"
              name="apt-suite"
              placeholder="Apt 2B, Unit 5, etc."
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
        )}

        <div className="action-row">
          <button
            type="button"
            className="btn btn--full-width js-next-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? 'CHECKING...' : 'GET STARTED'}
          </button>
        </div>

        <div className="action-row action-row--secondary mobile-hide">
          <button
            type="button"
            className="btn btn--outline btn--full-width js-check-order-btn"
            onClick={() => setShowOrderStatus(true)}
          >
            Check Order Status
          </button>
        </div>
      </div>

      <OrderStatusModal
        isOpen={showOrderStatus}
        onClose={() => setShowOrderStatus(false)}
      />
    </section>
  )
}
